import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, FileDown, Plus, Trash2 } from "lucide-react";
import { useToast } from "../../../context/ToastContext";
import { getErrorMessage } from "../../../api/client";
import * as tipiReportApi from "../../../api/tipiReport";
import { TipoReport } from "../../../api/tipiReport";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";

/**
 * Configurazione dei tipi di report — riservata agli admin (`report:configura`).
 *
 * Si sceglie quali sezioni un report contiene e in che ordine. Non si creano
 * tipi nuovi e non si cancellano: sono due, nascono dalla migrazione, e il
 * codice sceglie il generatore dalla loro chiave. Stesso stato vuoto e stessa
 * spiegazione della pagina Template Email, che ha lo stesso vincolo.
 */
export default function TipiReport() {
  const { showToast } = useToast();
  const [tipi, setTipi] = useState<TipoReport[]>([]);
  const [chiaveScelta, setChiaveScelta] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  /** Copia di lavoro delle sezioni attive: si salva solo quando si preme Salva. */
  const [sezioni, setSezioni] = useState<string[]>([]);

  /** Blocco dei destinatari fissi — solo sui tipi che partono da soli. */
  const [nuovaEmail, setNuovaEmail] = useState("");
  const [nuovoNome, setNuovoNome] = useState("");
  const [isAggiungendo, setIsAggiungendo] = useState(false);
  /** Mese dell'anteprima, vuoto = quello appena chiuso, come fa il job. */
  const [meseAnteprima, setMeseAnteprima] = useState("");
  const [isGenerando, setIsGenerando] = useState<"pdf" | "xlsx" | null>(null);

  async function load() {
    setIsLoading(true);
    try {
      const list = await tipiReportApi.listTipiReport();
      setTipi(list);
      setChiaveScelta((corrente) => corrente ?? list[0]?.chiave ?? null);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare i tipi di report."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scelto = tipi.find((t) => t.chiave === chiaveScelta) ?? null;

  useEffect(() => {
    if (scelto) setSezioni(scelto.sezioni);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scelto?.chiave, scelto?.updated_at]);

  const modificato = scelto !== null && JSON.stringify(sezioni) !== JSON.stringify(scelto.sezioni);

  function attiva(chiave: string, acceso: boolean) {
    // Accendendo, la sezione va in fondo: è l'unico posto che non richiede di
    // indovinare dove la voleva chi l'ha accesa. Da lì si sposta con le frecce.
    setSezioni((correnti) =>
      acceso ? [...correnti, chiave] : correnti.filter((c) => c !== chiave)
    );
  }

  function sposta(chiave: string, verso: -1 | 1) {
    setSezioni((correnti) => {
      const i = correnti.indexOf(chiave);
      const j = i + verso;
      if (i < 0 || j < 0 || j >= correnti.length) return correnti;
      const copia = [...correnti];
      [copia[i], copia[j]] = [copia[j], copia[i]];
      return copia;
    });
  }

  async function salva() {
    if (!scelto) return;
    setIsSaving(true);
    try {
      const aggiornato = await tipiReportApi.updateTipoReport(scelto.chiave, { sezioni });
      setTipi((correnti) => correnti.map((t) => (t.chiave === aggiornato.chiave ? aggiornato : t)));
      showToast("Sezioni salvate.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare le sezioni."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  async function aggiungiDestinatario() {
    if (!scelto) return;
    setIsAggiungendo(true);
    try {
      await tipiReportApi.aggiungiDestinatario(scelto.chiave, { email: nuovaEmail, nome: nuovoNome });
      setNuovaEmail("");
      setNuovoNome("");
      // Si ricarica invece di aggiungere la riga a mano: il backend normalizza
      // l'indirizzo (minuscolo, spazi tolti), e mettere in elenco quello
      // digitato mostrerebbe per qualche secondo una cosa diversa da quella
      // salvata.
      await load();
      showToast("Destinatario aggiunto.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile aggiungere il destinatario."), "error");
    } finally {
      setIsAggiungendo(false);
    }
  }

  async function cambiaDestinatario(id: string, attivo: boolean) {
    if (!scelto) return;
    try {
      await tipiReportApi.aggiornaDestinatario(scelto.chiave, id, { attivo });
      await load();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile aggiornare il destinatario."), "error");
    }
  }

  async function eliminaDestinatario(id: string, email: string) {
    if (!scelto) return;
    // Conferma esplicita: togliere un indirizzo da un invio automatico si
    // scopre solo il mese dopo, quando quella persona non riceve piu' niente.
    if (!window.confirm(`Togliere ${email} dai destinatari? Non ricevera' piu' questo report.`)) return;
    try {
      await tipiReportApi.eliminaDestinatario(scelto.chiave, id);
      await load();
      showToast("Destinatario rimosso.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile rimuovere il destinatario."), "error");
    }
  }

  async function generaAdesso(formato: "pdf" | "xlsx") {
    if (!scelto) return;
    setIsGenerando(formato);
    try {
      await tipiReportApi.generaAdesso(scelto.chiave, formato, meseAnteprima);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile generare il documento."), "error");
    } finally {
      setIsGenerando(null);
    }
  }

  async function cambiaAttivo(acceso: boolean) {
    if (!scelto) return;
    try {
      const aggiornato = await tipiReportApi.updateTipoReport(scelto.chiave, { attivo: acceso });
      setTipi((correnti) => correnti.map((t) => (t.chiave === aggiornato.chiave ? aggiornato : t)));
      showToast(acceso ? "Tipo attivato." : "Tipo disattivato: non comparirà fra quelli inviabili.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile cambiare lo stato."), "error");
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-ink">Tipi di report</h1>
        <p className="mt-1 text-sm text-ink/50">
          Di che cosa è fatto ogni report e in che ordine. Chi invia sceglie fra questi tipi e non ne compone di
          nuovi.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-ink/40">Caricamento…</p>
      ) : tipi.length === 0 ? (
        /* Stessa situazione della pagina Template Email a tabella vuota: i tipi
           sono righe create dalla migrazione, non dall'interfaccia. Senza questo
           messaggio la pagina sembrerebbe rotta o senza permessi. */
        <div className="rounded-card border border-line bg-white p-6 shadow-card">
          <h2 className="text-base font-bold text-ink">Nessun tipo di report</h2>
          <p className="mt-2 max-w-2xl text-sm text-ink/70">
            I tipi di report sono righe di archivio create dalla migrazione, non da questa pagina: dove quella
            migrazione non è ancora stata applicata non c'è nulla da elencare.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
          {/* --- Elenco tipi --- */}
          <div className="flex flex-col gap-2">
            {tipi.map((t) => (
              <button
                key={t.chiave}
                onClick={() => setChiaveScelta(t.chiave)}
                className={`flex items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                  t.chiave === chiaveScelta
                    ? "border-primary bg-primary/5 font-semibold text-primary"
                    : "border-line bg-white text-ink hover:bg-black/5"
                }`}
              >
                {t.nome}
                <Badge color={t.attivo ? "success" : "neutral"}>{t.attivo ? "Attivo" : "Disattivo"}</Badge>
              </button>
            ))}
          </div>

          {/* --- Sezioni del tipo scelto --- */}
          {scelto && (
            <div className="rounded-card border border-line bg-white p-5 shadow-card">
              <div className="mb-4 flex items-start justify-between gap-4 border-b border-line pb-4">
                <div>
                  <p className="text-base font-bold text-ink">{scelto.nome}</p>
                  {scelto.descrizione && <p className="mt-1 text-xs text-ink/60">{scelto.descrizione}</p>}
                </div>
                <label className="flex shrink-0 items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-black/20"
                    checked={scelto.attivo}
                    onChange={(e) => cambiaAttivo(e.target.checked)}
                  />
                  Tipo attivo
                </label>
              </div>

              <p className="mb-1 text-sm font-medium text-ink">Sezioni del documento</p>
              <p className="mb-3 text-xs text-ink/60">
                Le sezioni spente non compaiono nel PDF. L'ordine qui sotto è l'ordine in cui vengono stampate.
              </p>

              <ul className="flex flex-col gap-2">
                {/* Prima le attive nel loro ordine, poi le spente: così l'elenco
                    si legge come il documento, invece di essere un catalogo in cui
                    l'ordine reale va ricostruito a mente. */}
                {[
                  ...sezioni
                    .map((c) => scelto.sezioni_disponibili.find((s) => s.chiave === c))
                    .filter((s): s is NonNullable<typeof s> => s !== undefined),
                  ...scelto.sezioni_disponibili.filter((s) => !sezioni.includes(s.chiave)),
                ].map((s) => {
                  const accesa = sezioni.includes(s.chiave);
                  const posizione = sezioni.indexOf(s.chiave);
                  return (
                    <li
                      key={s.chiave}
                      className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${
                        accesa ? "border-line bg-white" : "border-dashed border-line bg-black/[0.02]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-black/20"
                        checked={accesa}
                        // L'intestazione non si spegne: un documento senza
                        // l'anagrafica di chi riguarda non dice di chi sia.
                        disabled={s.sempre}
                        onChange={(e) => attiva(s.chiave, e.target.checked)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 text-sm font-medium text-ink">
                          {s.etichetta}
                          {s.sempre && <Badge color="neutral">Sempre presente</Badge>}
                        </span>
                        <span className="mt-0.5 block text-xs text-ink/55">{s.descrizione}</span>
                      </span>

                      {/* Frecce invece del trascinamento: `@dnd-kit/sortable` non
                          è fra le dipendenze (c'è solo `core`, per il calendario)
                          e aggiungerlo per riordinare sei voci sarebbe sproporzionato.
                          Le frecce funzionano anche da tastiera. */}
                      {accesa && (
                        <span className="flex shrink-0 gap-1">
                          <button
                            type="button"
                            aria-label={`Sposta ${s.etichetta} più in alto`}
                            disabled={posizione <= 0}
                            onClick={() => sposta(s.chiave, -1)}
                            className="rounded border border-line p-1 text-ink/60 hover:bg-black/5 disabled:opacity-30"
                          >
                            <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Sposta ${s.etichetta} più in basso`}
                            disabled={posizione === sezioni.length - 1}
                            onClick={() => sposta(s.chiave, 1)}
                            className="rounded border border-line p-1 text-ink/60 hover:bg-black/5 disabled:opacity-30"
                          >
                            <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>

              <div className="mt-4 flex items-center justify-end gap-2 border-t border-line pt-4">
                {modificato && (
                  <button
                    type="button"
                    onClick={() => setSezioni(scelto.sezioni)}
                    className="text-xs font-semibold text-primary hover:underline"
                  >
                    Annulla modifiche
                  </button>
                )}
                <Button type="button" loading={isSaving} disabled={!modificato} onClick={salva}>
                  Salva
                </Button>
              </div>

              {/* --- Solo per i report che partono da soli ------------------ */}
              {scelto.automatico && (
                <>
                  <div className="mt-6 border-t border-line pt-5">
                    <p className="text-sm font-medium text-ink">Destinatari fissi</p>
                    <p className="mb-3 text-xs text-ink/60">
                      Indirizzi liberi, non utenti dell'app: questo report parte da solo il primo di ogni mese, sul
                      mese appena chiuso, e va a chi è elencato qui.
                    </p>

                    {scelto.destinatari.length === 0 ? (
                      /* Non è uno stato vuoto qualunque: finché è così il job
                         non ha a chi mandare ed esce con un errore, quindi lo
                         dice invece di mostrare una lista vuota e basta. */
                      <p className="mb-3 rounded-lg border border-dashed border-line bg-black/[0.02] px-3 py-2.5 text-xs text-ink/70">
                        Nessun destinatario: finché questo elenco è vuoto il report <strong>non parte</strong>.
                      </p>
                    ) : (
                      <ul className="mb-3 flex flex-col gap-2">
                        {scelto.destinatari.map((d) => (
                          <li
                            key={d.id}
                            className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
                              d.attivo ? "border-line bg-white" : "border-dashed border-line bg-black/[0.02]"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 shrink-0 rounded border-black/20"
                              checked={d.attivo}
                              aria-label={`Manda a ${d.email}`}
                              onChange={(e) => cambiaDestinatario(d.id, e.target.checked)}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm text-ink">{d.email}</span>
                              {d.nome && <span className="block truncate text-xs text-ink/55">{d.nome}</span>}
                            </span>
                            {!d.attivo && <Badge color="neutral">In pausa</Badge>}
                            <button
                              type="button"
                              aria-label={`Togli ${d.email}`}
                              onClick={() => eliminaDestinatario(d.id, d.email)}
                              className="rounded border border-line p-1 text-ink/50 hover:bg-black/5 hover:text-accent"
                            >
                              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="flex flex-wrap items-end gap-3">
                      <div className="min-w-[220px] flex-1">
                        <Input
                          label="Email"
                          type="email"
                          placeholder="amministrazione@evo.it"
                          value={nuovaEmail}
                          onChange={(e) => setNuovaEmail(e.target.value)}
                        />
                      </div>
                      <div className="min-w-[160px] flex-1">
                        <Input
                          label="Nome (facoltativo)"
                          value={nuovoNome}
                          onChange={(e) => setNuovoNome(e.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        loading={isAggiungendo}
                        disabled={!nuovaEmail.trim()}
                        onClick={aggiungiDestinatario}
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        Aggiungi
                      </Button>
                    </div>
                  </div>

                  {/* --- Anteprima: quello che il job manderebbe ------------- */}
                  <div className="mt-6 border-t border-line pt-5">
                    <p className="text-sm font-medium text-ink">Genera adesso</p>
                    <p className="mb-3 text-xs text-ink/60">
                      Scarica quello che il job manderebbe, <strong>senza mandarlo</strong> e senza segnare il mese
                      come già fatto. Serve a guardarlo prima del primo invio automatico.
                    </p>
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="w-[180px]">
                        <Input
                          label="Mese"
                          type="month"
                          value={meseAnteprima}
                          onChange={(e) => setMeseAnteprima(e.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        loading={isGenerando === "pdf"}
                        onClick={() => generaAdesso("pdf")}
                      >
                        <FileDown className="h-4 w-4" aria-hidden="true" />
                        PDF
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        loading={isGenerando === "xlsx"}
                        onClick={() => generaAdesso("xlsx")}
                      >
                        <FileDown className="h-4 w-4" aria-hidden="true" />
                        Foglio di calcolo
                      </Button>
                      <p className="text-xs text-ink/50">Vuoto = il mese appena chiuso, come fa il job.</p>
                    </div>
                  </div>

                  {/* --- L'ultima volta che è partito ------------------------ */}
                  <div className="mt-6 border-t border-line pt-5">
                    <p className="mb-2 text-sm font-medium text-ink">Ultimo invio</p>
                    {scelto.ultima_esecuzione === null ? (
                      <p className="text-xs text-ink/60">Non è ancora mai partito.</p>
                    ) : (
                      <p className="text-xs text-ink/70">
                        <Badge
                          color={
                            scelto.ultima_esecuzione.stato === "ok"
                              ? "success"
                              : scelto.ultima_esecuzione.stato === "parziale"
                                ? "warning"
                                : "danger"
                          }
                        >
                          {scelto.ultima_esecuzione.stato}
                        </Badge>{" "}
                        periodo {scelto.ultima_esecuzione.periodo_da.slice(0, 10)} —{" "}
                        {scelto.ultima_esecuzione.periodo_a.slice(0, 10)}, inviati{" "}
                        {scelto.ultima_esecuzione.destinatari_ok}, falliti {scelto.ultima_esecuzione.destinatari_ko}.
                        {scelto.ultima_esecuzione.errore && (
                          <span className="mt-1 block text-accent">{scelto.ultima_esecuzione.errore}</span>
                        )}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
